/**
 * Social Media Page - Links and embedded feeds from Facebook, Instagram, and X
 * Uses official SDKs for proper embedding
 */
import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Facebook, Instagram, ExternalLink, Heart, Share2, Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { SOCIAL_ACCOUNTS, XIcon } from "../lib/social";

// Fallback card with direct link to social profile
const SocialFeedCard = ({ platform, Icon, color, colorBg, url, handle, description }) => (
  <div className={`min-h-[380px] flex flex-col items-center justify-center p-6 text-center ${colorBg}`} data-testid={`${platform.toLowerCase()}-embed`}>
    <div className={`w-20 h-20 ${color} rounded-full flex items-center justify-center mb-5 shadow-lg`}>
      <Icon className="w-10 h-10 text-white" />
    </div>
    <h3 className="text-xl font-bold text-stone-800 mb-2">Follow us on {platform}!</h3>
    <p className="text-stone-600 text-sm mb-5 max-w-[280px] leading-relaxed">
      {description}
    </p>
    <a href={url} target="_blank" rel="noopener noreferrer">
      <Button className={`${color} text-white shadow-md hover:shadow-lg transition-shadow`}>
        <ExternalLink className="w-4 h-4 mr-2" />
        Visit {handle}
      </Button>
    </a>
  </div>
);

// Facebook Page Plugin using official SDK (XFBML)
const FacebookPageEmbed = () => {
  const [loading, setLoading] = useState(true);
  const [showFallback, setShowFallback] = useState(false);
  const containerRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    
    // Initialize Facebook SDK
    window.fbAsyncInit = function() {
      window.FB.init({
        xfbml: true,
        version: 'v20.0'
      });
      // Parse the plugin after SDK loads
      if (containerRef.current && window.FB) {
        window.FB.XFBML.parse(containerRef.current, () => {
          if (mountedRef.current) {
            setLoading(false);
            // Check if iframe actually loaded content
            setTimeout(() => {
              const iframe = containerRef.current?.querySelector('iframe');
              if (!iframe || iframe.offsetHeight < 100) {
                setShowFallback(true);
              }
            }, 2000);
          }
        });
      }
    };

    // Load Facebook SDK script
    if (!document.getElementById('facebook-jssdk')) {
      const script = document.createElement('script');
      script.id = 'facebook-jssdk';
      script.src = 'https://connect.facebook.net/en_US/sdk.js';
      script.async = true;
      script.defer = true;
      script.crossOrigin = 'anonymous';
      document.body.appendChild(script);
    } else if (window.FB) {
      // SDK already loaded, just parse
      window.FB.XFBML.parse(containerRef.current, () => {
        if (mountedRef.current) {
          setLoading(false);
        }
      });
    }

    // Timeout fallback - show fallback card if SDK doesn't load in time
    const timeout = setTimeout(() => {
      if (mountedRef.current) {
        setLoading(false);
        const iframe = containerRef.current?.querySelector('iframe');
        if (!iframe || iframe.offsetHeight < 100) {
          setShowFallback(true);
        }
      }
    }, 12000);

    return () => {
      mountedRef.current = false;
      clearTimeout(timeout);
    };
  }, []);

  if (showFallback) {
    return (
      <SocialFeedCard
        platform="Facebook"
        Icon={Facebook}
        color="bg-blue-600"
        colorBg="bg-gradient-to-br from-blue-50 to-blue-100"
        url={SOCIAL_ACCOUNTS.facebook.url}
        handle="CatPalsPlus"
        description="Follow us for daily updates, cat photos, adoption announcements, and community events!"
      />
    );
  }

  return (
    <div 
      ref={containerRef} 
      className="min-h-[380px] bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center p-2 overflow-hidden relative" 
      data-testid="facebook-embed"
    >
      {loading && (
        <div className="absolute inset-0 flex flex-col items-center justify-center z-10 bg-gradient-to-br from-blue-50 to-blue-100">
          <Loader2 className="w-8 h-8 text-blue-400 animate-spin mb-3" />
          <p className="text-stone-500 text-sm">Loading Facebook feed...</p>
        </div>
      )}
      <div 
        className="fb-page" 
        data-href="https://www.facebook.com/CatPalsPlus"
        data-tabs="timeline"
        data-width="340"
        data-height="380"
        data-small-header="false"
        data-adapt-container-width="true"
        data-hide-cover="false"
        data-show-facepile="true"
      >
        <blockquote cite="https://www.facebook.com/CatPalsPlus" className="fb-xfbml-parse-ignore">
          <a href="https://www.facebook.com/CatPalsPlus">CatPals+ on Facebook</a>
        </blockquote>
      </div>
    </div>
  );
};

// Twitter/X Timeline Embed using official widget
const TwitterTimelineEmbed = () => {
  const [loading, setLoading] = useState(true);
  const [showFallback, setShowFallback] = useState(false);
  const containerRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;

    const loadTwitterWidget = () => {
      if (window.twttr && window.twttr.widgets) {
        window.twttr.widgets.load(containerRef.current);
        // Check if widget rendered after a delay
        setTimeout(() => {
          if (mountedRef.current) {
            setLoading(false);
            // Check if the timeline actually rendered
            const rendered = containerRef.current?.querySelector('.twitter-timeline-rendered, iframe');
            if (!rendered) {
              setShowFallback(true);
            }
          }
        }, 5000);
      }
    };

    // Load Twitter widgets script
    if (!document.getElementById('twitter-wjs')) {
      window.twttr = window.twttr || {};
      window.twttr._e = window.twttr._e || [];
      window.twttr.ready = function(f) {
        window.twttr._e.push(f);
      };
      
      const script = document.createElement('script');
      script.id = 'twitter-wjs';
      script.src = 'https://platform.twitter.com/widgets.js';
      script.async = true;
      script.onload = loadTwitterWidget;
      document.body.appendChild(script);
    } else {
      loadTwitterWidget();
    }

    // Timeout fallback
    const timeout = setTimeout(() => {
      if (mountedRef.current) {
        setLoading(false);
        const rendered = containerRef.current?.querySelector('.twitter-timeline-rendered, iframe');
        if (!rendered) {
          setShowFallback(true);
        }
      }
    }, 12000);

    return () => {
      mountedRef.current = false;
      clearTimeout(timeout);
    };
  }, []);

  if (showFallback) {
    return (
      <SocialFeedCard
        platform="X"
        Icon={XIcon}
        color="bg-black"
        colorBg="bg-gradient-to-br from-gray-50 to-gray-100"
        url={SOCIAL_ACCOUNTS.x.url}
        handle="@CatPals_Plus"
        description="Get quick updates, join conversations, and share your love for cats with our community!"
      />
    );
  }

  return (
    <div 
      ref={containerRef} 
      className="min-h-[380px] bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-2 overflow-hidden relative" 
      data-testid="twitter-embed"
    >
      {loading && (
        <div className="absolute inset-0 flex flex-col items-center justify-center z-10 bg-gradient-to-br from-gray-50 to-gray-100">
          <Loader2 className="w-8 h-8 text-gray-400 animate-spin mb-3" />
          <p className="text-stone-500 text-sm">Loading X feed...</p>
        </div>
      )}
      <a 
        className="twitter-timeline" 
        data-height="380"
        data-width="340"
        data-theme="light"
        data-chrome="noheader nofooter noborders"
        href="https://twitter.com/CatPals_Plus"
      >
        Tweets by @CatPals_Plus
      </a>
    </div>
  );
};

// Instagram Feed Card - Instagram embeds require oEmbed API with access token
const InstagramFeedCard = () => {
  return (
    <SocialFeedCard
      platform="Instagram"
      Icon={Instagram}
      color="bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400"
      colorBg="bg-gradient-to-br from-purple-50 to-pink-50"
      url={SOCIAL_ACCOUNTS.instagram.url}
      handle="@CatPals_Plus"
      description="See adorable cat photos, behind-the-scenes stories, and heartwarming rescue moments!"
    />
  );
};

const SocialMediaPage = () => {
  // Social accounts with descriptions for cards
  const socialAccounts = [
    { ...SOCIAL_ACCOUNTS.facebook, description: "Follow us for daily updates, cat photos, adoption announcements, and community events!" },
    { ...SOCIAL_ACCOUNTS.instagram, description: "See adorable cat photos, behind-the-scenes stories, and heartwarming rescue moments!" },
    { ...SOCIAL_ACCOUNTS.x, name: "X (Twitter)", description: "Get quick updates, join conversations, and share your love for cats with our community!" }
  ];

  return (
    <div className="min-h-screen pt-24 sm:pt-36 bg-gradient-to-br from-amber-50 via-stone-50 to-amber-50" data-testid="social-media-page">
      <div className="max-w-6xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="w-20 h-20 bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
            <Share2 className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-stone-900 mb-4">Connect With Us</h1>
          <p className="text-stone-600 max-w-2xl mx-auto text-lg">
            Follow CatPals+ on social media for adorable cat content, adoption updates, 
            rescue stories, and to join our amazing community of cat lovers!
          </p>
        </div>

        {/* Social Media Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-16">
          {socialAccounts.map(social => (
            <Card key={social.name} className="overflow-hidden hover:shadow-lg transition-shadow">
              <CardHeader className={`${social.color} text-white`}>
                <div className="flex items-center gap-3">
                  <social.icon className="w-8 h-8" />
                  <div>
                    <CardTitle className="text-xl">{social.name}</CardTitle>
                    <p className="text-white/80 text-sm">{social.handle}</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                <p className="text-stone-600 mb-4">{social.description}</p>
                <a
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block"
                  data-testid={`social-follow-${social.name.toLowerCase().replace(/[^a-z]+/g, '-').replace(/-$/, '')}`}
                >
                  <Button className={`${social.color} text-white`}>
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Follow on {social.name}
                  </Button>
                </a>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Embedded Feeds Section */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-stone-900 mb-6 text-center">Latest from Our Social Media</h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Facebook */}
            <Card className="overflow-hidden shadow-md">
              <CardHeader className="bg-blue-600 text-white py-3">
                <div className="flex items-center gap-2">
                  <Facebook className="w-5 h-5" />
                  <span className="font-semibold">Facebook</span>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <FacebookPageEmbed />
              </CardContent>
            </Card>

            {/* Instagram */}
            <Card className="overflow-hidden shadow-md">
              <CardHeader className="bg-gradient-to-r from-purple-600 via-pink-500 to-orange-400 text-white py-3">
                <div className="flex items-center gap-2">
                  <Instagram className="w-5 h-5" />
                  <span className="font-semibold">Instagram</span>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <InstagramFeedCard />
              </CardContent>
            </Card>

            {/* X (Twitter) */}
            <Card className="overflow-hidden shadow-md">
              <CardHeader className="bg-black text-white py-3">
                <div className="flex items-center gap-2">
                  <XIcon className="w-5 h-5" />
                  <span className="font-semibold">X (Twitter)</span>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <TwitterTimelineEmbed />
              </CardContent>
            </Card>
          </div>
          
          {/* Note about embeds */}
          <p className="text-center text-stone-500 text-sm mt-6">
            Click the buttons above to visit our profiles and see all our latest posts!
          </p>
        </div>

        {/* Call to Action */}
        <div className="text-center bg-gradient-to-r from-amber-100 to-orange-100 rounded-2xl p-8 md:p-12 shadow-md">
          <Heart className="w-12 h-12 text-amber-600 mx-auto mb-4" />
          <h2 className="text-2xl md:text-3xl font-bold text-stone-900 mb-4">
            Join Our Cat-Loving Community!
          </h2>
          <p className="text-stone-600 max-w-2xl mx-auto mb-6">
            By following us on social media, you help us spread awareness about cat rescue and adoption. 
            Share our posts, tag your friends, and help us find loving homes for more cats!
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/cats">
              <Button className="bg-amber-600 hover:bg-amber-700">
                View Cats for Adoption
              </Button>
            </Link>
            <Link to="/donate">
              <Button variant="outline" className="border-amber-600 text-amber-700 hover:bg-amber-50">
                <Heart className="w-4 h-4 mr-2" />
                Support Our Mission
              </Button>
            </Link>
          </div>
        </div>

        {/* Share Prompt */}
        <div className="mt-8 text-center text-stone-500 text-sm">
          <p>Got photos of your adopted CatPals+ cat? Tag us on social media!</p>
          <p className="font-medium text-amber-600 mt-1">#CatPalsPlus #NorwichCatRescue #AdoptDontShop</p>
        </div>
      </div>
    </div>
  );
};

export default SocialMediaPage;
